// pages/monitor/panels/ems/EmsWiring.js
// 《云平台（能源管家）需求文档》2.7.2 主接线图（p17，截图 p017_i015）
// 差异：普通储能为单一接线图（不做强电图/拓扑图拆分）；结构为 AC母线 → PCS → 电池堆 → 电池簇
// 的垂直链式拓扑，节点绿描边（正常）、红色竖线为功率通路，每个节点带数据面板。
import React from 'react';
import { Tag } from 'antd';
import { getDevices, getStation } from '../../../../config/stationData';
import { useLang, t } from '../../consoles/ems/langContext';

// 节点数据面板（对齐 p017_i015：白字左对齐键值）
function Node({ x, y, w, h, title, rows, alarm, nodeColor }) {
  const { lang } = useLang();
  return (
    <g>
      <rect
        x={x} y={y} width={w} height={h} rx={10}
        fill="rgba(13,31,68,0.85)"
        stroke={alarm ? '#ff5a7f' : (nodeColor || 'rgba(0,220,160,0.6)')}
        strokeWidth={alarm ? 1.6 : 1.4}
      />
      <text x={x + w / 2} y={y + 22} textAnchor="middle" className="wg-nm">{title}</text>
      {rows.map((r, i) => (
        <text key={i} x={x + 14} y={y + 40 + i * 15} className="wg-row">
          <tspan className="wg-k">{t(lang, r[0])}</tspan>
          <tspan className="wg-v">{r[1]}</tspan>
        </text>
      ))}
      {alarm ? (
        <g>
          <circle cx={x + w - 8} cy={y} r={8} fill="#ff5a7f" />
          <text x={x + w - 8} y={y + 4} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={700}>!</text>
        </g>
      ) : null}
    </g>
  );
}

export default function EmsWiring({ station }) {
  const { lang } = useLang();
  const st = station || getStation('xz');
  const dev = getDevices(st.id);
  const pcs = dev.pcs;
  const racks = dev.racks;
  const p = pcs[0];
  const r = racks[0];
  const pcsAlarm = pcs.some((x) => x.alarm);
  const rackAlarm = racks.some((x) => x.alarm);
  const CX = 340; // 中心列
  const NW = 300; // 节点宽
  const BWH = 84;

  return (
    <div className="mod-body">
      <div className="panel">
        <div className="panel-title">
          <span>{t(lang, '主接线图', 'Wiring Diagram')}</span>
          <small>{t(lang, '单线图 · 实时数据（AC母线 → PCS → 电池堆 → 电池簇）', 'Single-line · Real-time Data (AC Bus → PCS → Battery Pack → Battery Cluster)')}</small>
        </div>
        <div className="wg-wrap">
          <svg viewBox="0 0 720 560" className="wg-svg" style={{ minWidth: 560 }} role="img" aria-label="主接线图">
            <defs>
              <marker id="wg-arrow" markerWidth="9" markerHeight="9" refX="6" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill="rgba(0,180,216,0.7)" />
              </marker>
            </defs>

            {/* AC 交流母线（红色主干，水平贯穿顶部） */}
            <line x1={40} y1={60} x2={680} y2={60} stroke="#ff5a5f" strokeWidth={5} />
            <text x={44} y={46} className="wg-tip">{t(lang, `AC 交流母线 (${st.grid && st.grid.voltage ? st.grid.voltage : '0.4 kV'})`, `AC Bus (${st.grid && st.grid.voltage ? st.grid.voltage : '0.4 kV'})`)}</text>

            {/* 母线 → PCS */}
            <line x1={CX + NW / 2} y1={60} x2={CX + NW / 2} y2={130} stroke="#ff5a5f" strokeWidth={2} markerEnd="url(#wg-arrow)" />

            {/* 1#PCS */}
            <Node
              x={CX - NW / 2} y={130} w={NW} h={BWH}
              title="1#PCS"
              rows={[
                [t(lang, '有功功率', 'Active Power'), `${p.p.toFixed(2)} kW`],
                [t(lang, '无功功率', 'Reactive Power'), `${p.q.toFixed(2)} kVar`],
              ]}
              alarm={pcsAlarm}
            />

            {/* PCS → 电池堆（红色功率通路线） */}
            <line x1={CX + NW / 2} y1={214} x2={CX + NW / 2} y2={274} stroke="#ff5a5f" strokeWidth={2} markerEnd="url(#wg-arrow)" />

            {/* 1#电池堆 */}
            <Node
              x={CX - NW / 2} y={274} w={NW} h={BWH + 2}
              title="1#电池堆"
              rows={[
                [t(lang, 'SOC', 'SOC'), `${r.soc.toFixed(2)} %`],
                [t(lang, '电压', 'Voltage'), `${r.voltage.toFixed(2)} V`],
                [t(lang, '电流', 'Current'), `${(-r.current).toFixed(2)} A`],
              ]}
              alarm={rackAlarm}
            />

            {/* 电池堆 → 电池簇 */}
            <line x1={CX + NW / 2} y1={360} x2={CX + NW / 2} y2={420} stroke="#ff5a5f" strokeWidth={2} markerEnd="url(#wg-arrow)" />

            {/* 1#电池堆-1#电池簇 */}
            <Node
              x={CX - NW / 2} y={420} w={NW} h={BWH + 2}
              title="1#电池堆-1#电池簇"
              rows={[
                [t(lang, 'SOC', 'SOC'), `${r.soc.toFixed(2)} %`],
                [t(lang, '电压', 'Voltage'), `${r.voltage.toFixed(2)} V`],
                [t(lang, '电流', 'Current'), `${(-r.current + 1.3).toFixed(2)} A`],
              ]}
              alarm={rackAlarm}
            />
          </svg>
        </div>
        <div className="wg-legend">
          <span><span className="dot online" />{t(lang, '在线（绿色描边）', 'Online (Green Outline)')}</span>
          <span><span className="dot fault" />{t(lang, '告警', 'Alarm')}</span>
          <span>{t(lang, '红色线为功率通路（带电）', 'Red Lines Indicate Power Path (Energized)')}</span>
          <span>{t(lang, '箭头示意能量流向', 'Arrows Show Energy Flow')}</span>
        </div>
        <div className="ems-note">
          {t(lang, '接线图配置：可在系统管理后台配置现场接线情况，拖拽元件并绑定设备，展示相关实时数据。', 'Wiring Config: Configure on-site wiring in the system admin backend; drag components and bind devices to display real-time data.')}
        </div>
        <div className="dev-tags" style={{ marginTop: 10 }}>
          <Tag color="blue">{t(lang, '单一接线图', 'Single-line Diagram')}</Tag>
          <Tag>{t(lang, 'AC母线 / PCS / 电池堆 / 电池簇', 'AC Bus / PCS / Battery Pack / Battery Cluster')}</Tag>
          <Tag>{t(lang, `PCS ${pcs.length} 台`, `PCS ${pcs.length} Units`)}</Tag>
          <Tag>{t(lang, `电池堆 ${racks.length} 堆`, `Battery Packs ${racks.length}`)}</Tag>
        </div>
      </div>
    </div>
  );
}